import {
  Badge,
  BadgeLevel,
  BadgeShape,
  BadgeSize,
  ImpactBolt,
  Skeleton,
  Text,
  TextColor,
  TextVariant,
} from '@vizit/atoms';
import { ArrowCompactDownIcon, ArrowCompactUpIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { containerClasses, getScoreColor, getScoreColorRange } from './styles';
import type { ScoreMetricCellProps } from './types';

/**
 * Generic score metric cell displaying a score out of 100
 * with impact bolts and an optional score diff badge.
 * Score number color is based on score range:
 * 1-39 = red, 40-59 = orange, 60-79 = yellow-green, 80-100 = green.
 *
 * @example
 * <ScoreMetricCell label="CAROUSEL SCORE" score={32} impactLevel="high" />
 * <ScoreMetricCell label="HERO ASSET SCORE" score={32} impactLevel="medium" scoreDiff={5} />
 */
const ScoreMetricCell = React.forwardRef<HTMLDivElement, ScoreMetricCellProps>((props, forwardedRef) => {
  const {
    label,
    score,
    impactLevel,
    scoreDiff,
    isDisabled = false,
    isFocused = false,
    isLoading = false,
    className,
    ...other
  } = props;

  if (isLoading) {
    return (
      <div
        ref={forwardedRef}
        className={cx(containerClasses({ state: 'default' }), className)}
        data-testid="score-metric-cell"
        {...other}
      >
        <div className="flex items-start justify-between w-full">
          <Skeleton variant="square" className="h-5 w-24" />
          <Skeleton variant="square" className="h-7 w-28" />
        </div>
        <div className="flex flex-col gap-2">
          <Skeleton variant="square" className="h-[42px] w-20" />
          <Skeleton variant="square" className="h-3 w-16" />
        </div>
      </div>
    );
  }

  const hasDiff = scoreDiff != null && scoreDiff !== 0;
  const isPositiveDiff = hasDiff && scoreDiff > 0;
  const colorRange = getScoreColorRange(score);
  const state = isDisabled ? 'disabled' : isFocused ? 'focused' : 'default';

  return (
    <div
      ref={forwardedRef}
      className={cx(containerClasses({ state }), className)}
      data-testid="score-metric-cell"
      {...other}
    >
      <div className="flex items-start justify-between w-full">
        <Text
          variant={TextVariant.MonoXs}
          color={TextColor.Quaternary}
          className={isDisabled ? 'text-fg-disabled-subtle' : ''}
          showEllipsis={false}
        >
          {label}
        </Text>
        <ImpactBolt level={impactLevel} hideUnfilled isDisabled={isDisabled} />
      </div>

      <div className="flex items-center gap-2">
        <div className="flex flex-col gap-0.5">
          <Text
            variant={TextVariant.MonoXl}
            elementType="span"
            color={TextColor.Inherit}
            className={getScoreColor(colorRange, isDisabled)}
            showEllipsis={false}
          >
            {score}
          </Text>
          <Text
            variant={TextVariant.MonoSm}
            color={TextColor.Tertiary}
            className={isDisabled ? 'text-fg-disabled-subtle' : ''}
            showEllipsis={false}
          >
            /100
          </Text>
        </div>
        {hasDiff && (
          <div className={isDisabled ? 'opacity-20' : ''}>
            <Badge
              level={isPositiveDiff ? BadgeLevel.Success : BadgeLevel.Error}
              size={BadgeSize.Xs}
              shape={BadgeShape.Box}
              bordered={false}
            >
              {isPositiveDiff ? <ArrowCompactUpIcon size={8} /> : <ArrowCompactDownIcon size={8} />}
              {Math.abs(scoreDiff)}
            </Badge>
          </div>
        )}
      </div>
    </div>
  );
});

ScoreMetricCell.displayName = 'ScoreMetricCell';

export { ScoreMetricCell };
export type { ScoreMetricCellProps };
